import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import RadioGroup from "../../AddInvestmentModal/fields/RadioGroup";
import { Button, SelectComponent, Textarea, Checkbox } from "shared";
import { Input, DropFileInput } from "shared";
import { InvestmentItem } from "../../../types";
import { PreciousMetalsValues } from "./types";
import { preciousMetalsSchema } from "./validationSchemas";
import { RELIANCE_CONSENT_LABEL } from "./constants";

const PROPERTY_TYPE_OPTIONS = [
  { value: "gold", label: "Złoto" },
  { value: "silver", label: "Srebro" },
  { value: "other", label: "Inne" },
];

interface PreciousMetalsFormProps {
  item: InvestmentItem;
  onSubmit: (values: PreciousMetalsValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
}

const PreciousMetalsForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: PreciousMetalsFormProps) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const propertyType = String(item.details?.propertyType ?? "gold");

  const initialValues: PreciousMetalsValues = {
    propertyType,
    name: propertyType === "other" ? item.name ?? "" : "",
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    weight: item.details?.weight != null ? String(item.details.weight) : "",
    finess: String(item.details?.finess ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={preciousMetalsSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => {
        const currencyLabel =
          currencyOptions.find(
            (o: any) => o.value === formik.values.currencyUuid,
          )?.label ?? "";

        return (
          <Form>
            <ScrollToFirstError />
            <div className="modal-form__fields">
              <RadioGroup
                name="propertyType"
                label=""
                options={PROPERTY_TYPE_OPTIONS}
                value={formik.values.propertyType}
                onChange={(val) => formik.setFieldValue("propertyType", val)}
                error={
                  (formik.touched.propertyType || formik.submitCount > 0) &&
                  formik.errors.propertyType
                    ? formik.errors.propertyType
                    : undefined
                }
              />

              {formik.values.propertyType === "other" && (
                <Field
                  as={Input}
                  name="name"
                  label="Nazwa"
                  type="text"
                  error={
                    (formik.touched.name || formik.submitCount > 0) &&
                    formik.errors.name
                      ? formik.errors.name
                      : undefined
                  }
                />
              )}

              <Field
                as={Input}
                name="primaryValue"
                label="Cena zakupu"
                type="number"
                step="any"
                precision={2}
                maxLength={9}
                data-suffix={currencyLabel}
                error={
                  (formik.touched.primaryValue || formik.submitCount > 0) &&
                  formik.errors.primaryValue
                    ? formik.errors.primaryValue
                    : undefined
                }
              />

              <Field
                as={SelectComponent}
                name="currencyUuid"
                label="Waluta"
                options={currencyOptions}
                isSearchable={true}
                error={
                  (formik.touched.currencyUuid || formik.submitCount > 0) &&
                  formik.errors.currencyUuid
                    ? formik.errors.currencyUuid
                    : undefined
                }
              />

              <Field
                as={Input}
                name="weight"
                label="Masa"
                type="number"
                precision={0}
                maxLength={12}
                suffix="g"
                optional
              />

              <Field
                as={Input}
                name="finess"
                label="Próba"
                type="text"
                maxLength={6}
                optional
              />

              <Field
                component={Textarea}
                name="additionalInfo"
                label="Dodatkowe informacje"
                optional
              />

              <Field
                label="Umowa"
                name="contractFile"
                id="contractFile"
                as={DropFileInput}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                optional
                multiple={10}
                maxSize={5}
                description="Maksymalnie 10 plików, maks. 5 MB/plik (PDF, DOC, DOCX, JPG, PNG)"
                existingFiles={existingContractFiles.map((f: any) => ({
                  name: f.file?.name || "Plik",
                  url: f.file?.url || "",
                }))}
                onServerFileRemoved={onRemovedContract}
              />

              <Field
                as={Checkbox}
                name="relianceCanView"
                label={RELIANCE_CONSENT_LABEL}
                checked={formik.values.relianceCanView}
                onChange={() =>
                  formik.setFieldValue(
                    "relianceCanView",
                    !formik.values.relianceCanView,
                  )
                }
              />
            </div>

            <div className="modal-buttons">
              <Button
                type="button"
                className="button--stroke button--rounded"
                onClick={onCancel}
                label="Anuluj"
              />
              <Button
                type="submit"
                className="button--black button--rounded"
                onClick={() => {}}
                label={isPending ? "Zapisywanie..." : "Zapisz"}
                isLoading={isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PreciousMetalsForm;
